import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function AcceptInvite() {
  const { user, loading, login, register } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");

  const [preview, setPreview] = useState(null); // {brand_name, email, role, existing_user}
  const [previewError, setPreviewError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await api.post(`/invitations/preview`, { token });
        setPreview(data);
      } catch (err) {
        setPreviewError(err?.response?.data?.detail || "Deze uitnodiging is ongeldig of verlopen.");
      }
    })();
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/invitations/accept`, { token });
      setDone(true);
      toast.success("Je bent toegevoegd aan het team");
      setTimeout(() => navigate("/", { replace: true }), 900);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Accepteren mislukt");
    } finally {
      setBusy(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!password || password.length < 8) {
      toast.error("Wachtwoord moet minstens 8 tekens zijn");
      return;
    }
    setBusy(true);
    try {
      await register(preview.email, password, name || preview.email.split("@")[0]);
      await api.post(`/invitations/accept`, { token });
      setDone(true);
      toast.success("Account aangemaakt — welkom!");
      setTimeout(() => navigate("/", { replace: true }), 900);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aanmelden mislukt");
    } finally {
      setBusy(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(preview.email, password);
      await api.post(`/invitations/accept`, { token });
      setDone(true);
      toast.success("Welkom terug — je bent toegevoegd");
      setTimeout(() => navigate("/", { replace: true }), 900);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Inloggen mislukt");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <Center><Loader2 className="w-4 h-4 animate-spin" /> Laden…</Center>;
  }

  if (!token || previewError) {
    return (
      <Center>
        <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-sm">
          <h1 className="font-display text-2xl mb-2">Uitnodiging niet geldig</h1>
          <p className="text-sm text-gray-500">{previewError || "Vraag je uitnodiger om een nieuwe link."}</p>
        </div>
      </Center>
    );
  }

  if (!preview) {
    return <Center><Loader2 className="w-4 h-4 animate-spin" /> Uitnodiging ophalen…</Center>;
  }

  return (
    <div className="min-h-screen bg-[#f3f3f1] flex items-center justify-center p-6">
      <div data-testid="accept-invite-card" className="w-full max-w-md bg-white rounded-2xl p-8 shadow-sm">
        <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">The Brand Hub</div>
        <h1 className="font-display text-3xl font-medium text-[#1a1a1a] mb-2">Je bent uitgenodigd</h1>
        <p className="text-sm text-gray-600 mb-6">
          Voor <strong>{preview.brand_name}</strong> als <strong className="capitalize">{preview.role}</strong>.
          <br />We sturen alles naar <strong>{preview.email}</strong>.
        </p>

        {done ? (
          <div className="flex items-center gap-2 text-emerald-700">
            <CheckCircle2 className="w-5 h-5" /> Welkom aan boord!
          </div>
        ) : user && user.email?.toLowerCase() === preview.email.toLowerCase() ? (
          <Button
            data-testid="accept-invite-confirm"
            onClick={accept}
            disabled={busy}
            className="w-full h-11 rounded-lg bg-[#1a1a1a] text-white hover:bg-black/90 disabled:opacity-60"
          >
            {busy ? "Bezig…" : "Uitnodiging aanvaarden"}
          </Button>
        ) : user ? (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            Je bent ingelogd als {user.email}. Log uit en gebruik {preview.email} om deze uitnodiging te aanvaarden.
          </div>
        ) : preview.existing_user ? (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label>Wachtwoord</Label>
              <Input data-testid="invite-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 bg-white" />
            </div>
            <Button type="submit" data-testid="invite-login-submit" disabled={busy} className="w-full h-11 rounded-lg bg-[#1a1a1a] text-white hover:bg-black/90">
              {busy ? "Bezig…" : "Inloggen & aanvaarden"}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <Label>Je naam</Label>
              <Input data-testid="invite-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 bg-white" placeholder={preview.email.split("@")[0]} />
            </div>
            <div>
              <Label>Kies een wachtwoord (min. 8 tekens)</Label>
              <Input data-testid="invite-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 bg-white" />
            </div>
            <Button type="submit" data-testid="invite-signup-submit" disabled={busy} className="w-full h-11 rounded-lg bg-[#1a1a1a] text-white hover:bg-black/90">
              {busy ? "Bezig…" : "Account maken & aanvaarden"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}

function Center({ children }) {
  return <div className="min-h-screen bg-[#f3f3f1] flex items-center justify-center gap-2 text-[#1a1a1a] p-6">{children}</div>;
}
